<script setup lang="ts">
import CheckMarkIcon from '@/assets/icons/CheckMarkIcon.vue';
import MinusIcon from '@/assets/icons/MinusIcon.vue';
import { computed, ref, watch } from 'vue';

type TModelValue = boolean | 'intermediate';

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
}>();

const props = withDefaults(
  defineProps<{
    name?: string;
    modelValue?: TModelValue;
    disabled?: boolean;
    readonly?: boolean;
    subtle?: boolean;
    type?: 'ltr' | 'rtl';
    alignItems?: string;
  }>(),
  {
    name: undefined,
    modelValue: false,
    disabled: false,
    readonly: false,
    subtle: false,
    type: 'ltr',
    alignItems: 'start',
  },
);

const valueInternal = ref<TModelValue>(false);
const subtleChecked = computed(() => props.subtle && valueInternal.value !== false);
const surfaceClasses = computed(() => {
  if (valueInternal.value === false) return 'bg-white hover:bg-surface-secondary active:bg-surface-border';
  if (props.subtle) return 'bg-highlight hover:bg-surface-tint active:bg-surface-tint';
  return 'bg-tint hover:bg-tint-hover active:bg-tint-active';
});
const checkColorClasses = computed(() => {
  if (props.disabled) return 'text-secondary';
  return props.subtle ? 'text-tint' : 'text-white';
});

const changeValue = (): void => {
  if (props.disabled || props.readonly) return;
  valueInternal.value = !valueInternal.value;
  emit('update:modelValue', valueInternal.value);
};

const handleInputChange = (event: Event): void => {
  if (props.disabled || props.readonly) {
    event.preventDefault();
    return;
  }
  changeValue();
};

watch(
  () => props.modelValue,
  (val) => {
    valueInternal.value = val;
  },
  { immediate: true },
);
</script>

<template>
  <label
    class="flex"
    :class="[`items-${alignItems}`, type === 'rtl' && 'flex-row-reverse justify-between', 'gap-sm', 'custom-checkbox']"
  >
    <div
      :class="[
        'border rounded-checkbox shrink-0 cursor-pointer',
        subtleChecked ? 'border-tint/20' : 'border-divider',
        'relative h-icon-sm w-icon-sm duration-300',
        'focus-visible:outline-focus focus-visible:outline-offset-[3px]',
        surfaceClasses,
        disabled && '!bg-surface-tertiary cursor-not-allowed',
      ]"
      :tabindex="disabled || readonly ? -1 : 0"
      role="checkbox"
      :aria-checked="valueInternal === true || valueInternal === 'intermediate'"
      :disabled="disabled || readonly"
      @keypress.space="changeValue()"
      @keypress.enter="changeValue()"
    >
      <CheckMarkIcon v-if="valueInternal === true" :class="['absolute-center size-[14px]', checkColorClasses]" />
      <MinusIcon v-if="valueInternal === 'intermediate'" :class="['absolute-center size-[14px]', checkColorClasses]" />
      <input
        type="checkbox"
        :checked="valueInternal === true"
        :name="name"
        :disabled="disabled || readonly"
        @change="handleInputChange"
      />
    </div>
    <slot />
  </label>
</template>

<style scoped>
.custom-checkbox input[type='checkbox'] {
  display: none;
}
</style>
